/**
 * quick-ship-inventory.js
 *
 * Replaces the default "In Stock" inventory badge with a truck icon + "In Stock - Quick Ship"
 * across all page types (PDP, collection cards, featured collection, homepage).
 *
 * Two DOM patterns are handled:
 *   1. PDP  — <product-inventory data-inventory-level="normal|in_stock">
 *                 <span class="js-inventory-notice">In Stock</span>
 *             </product-inventory>
 *
 *   2. Cards — <div class="product-inventory__status" data-inventory-level="normal|in_stock">
 *                  In Stock
 *              </div>
 *
 * For pattern 1, product-inventory.js owns the element and updates both the
 * data-inventory-level attribute and innerText synchronously.  MutationObserver
 * callbacks are microtasks that fire after that synchronous block completes, so
 * our override always runs after product-inventory.js has finished writing.
 */
(function () {
  'use strict';

  var TRUCK_URL =
    'https://cdn.shopify.com/s/files/1/0717/0159/4403/files/Van_1.svg?v=1769712370';
  var ACCENT_COLOR = '#3d7c4b';
  var IN_STOCK_LEVELS = ['in_stock', 'normal', 'low', 'very_low'];

  /**
   * Inject a stylesheet once to kill the theme's green dot (::before on
   * .product-inventory__status) for in-stock items only.  We can't remove
   * pseudo-elements via JS, so a targeted CSS override is the right tool.
   */
  (function injectDotSuppression() {
    var style = document.createElement('style');
    style.textContent =
      'product-inventory[data-inventory-level="normal"] .product-inventory__status::before,' +
      'product-inventory[data-inventory-level="in_stock"] .product-inventory__status::before,' +
      'product-inventory[data-inventory-level="low"] .product-inventory__status::before,' +
      'product-inventory[data-inventory-level="very_low"] .product-inventory__status::before,' +
      'div.product-inventory__status[data-inventory-level="normal"]::before,' +
      'div.product-inventory__status[data-inventory-level="in_stock"]::before,' +
      'div.product-inventory__status[data-inventory-level="low"]::before,' +
      'div.product-inventory__status[data-inventory-level="very_low"]::before' +
      '{ display: none !important; }';
    document.head.appendChild(style);
  })();

  /**
   * Replace the content of a DOM node with truck icon + label.
   * @param {HTMLElement} container - the element whose content gets replaced
   * @param {object} sizes - { truckW, truckH, fontSize } for PDP vs card sizing
   */
  function renderQuickShip(container, sizes) {
    // Flex alignment on the container
    container.style.display = 'flex';
    container.style.alignItems = 'center';

    // Truck icon
    var truck = document.createElement('span');
    truck.setAttribute('aria-hidden', 'true');
    truck.style.cssText = [
      'display:inline-block',
      'flex-shrink:0',
      'width:' + sizes.truckW,
      'height:' + sizes.truckH,
      'margin-inline-end:8px',
      'background-color:' + ACCENT_COLOR,
      '-webkit-mask-image:url(' + TRUCK_URL + ')',
      'mask-image:url(' + TRUCK_URL + ')',
      '-webkit-mask-repeat:no-repeat',
      'mask-repeat:no-repeat',
      '-webkit-mask-size:contain',
      'mask-size:contain',
      'filter:drop-shadow(1px 0 0 ' + ACCENT_COLOR + ') ' +
        'drop-shadow(-1px 0 0 ' + ACCENT_COLOR + ') ' +
        'drop-shadow(0 1px 0 ' + ACCENT_COLOR + ') ' +
        'drop-shadow(0 -1px 0 ' + ACCENT_COLOR + ')',
    ].join(';');

    // Label
    var label = document.createElement('span');
    label.textContent = 'In Stock - Quick Ship';
    label.style.cssText = [
      'font-size:' + sizes.fontSize,
      'font-weight:575',
      'color:' + ACCENT_COLOR,
      'letter-spacing:normal',
      'line-height:1',
    ].join(';');

    container.innerHTML = '';
    container.appendChild(truck);
    container.appendChild(label);
  }

  // Size presets — tweak these to adjust appearance
  var SIZES = {
    pdp:  { truckW: '38px', truckH: '23px', fontSize: '14px' },
    card: { truckW: '30px', truckH: '18px', fontSize: '12px' },
  };

  /**
   * Given an element that carries data-inventory-level, decide whether to
   * apply the quick-ship badge and where to render it.
   */
  function processInventoryElement(el) {
    var level = el.dataset.inventoryLevel;
    if (!level || IN_STOCK_LEVELS.indexOf(level) === -1) return;

    if (el.tagName.toLowerCase() === 'product-inventory') {
      // PDP: render inside the notice span that product-inventory.js manages
      var notice = el.querySelector('.js-inventory-notice');
      if (notice) renderQuickShip(notice, SIZES.pdp);
      // Suppress urgency message (e.g. "Almost gone!") for low/very_low items
      var urgency = el.querySelector('.js-inventory-urgency');
      if (urgency) urgency.innerHTML = '';
    } else {
      // Cards: the element itself is the status container
      renderQuickShip(el, SIZES.card);
    }
  }

  /** Selector covering both DOM patterns */
  var SELECTOR =
    'product-inventory[data-inventory-level], ' +
    'div.product-inventory__status[data-inventory-level]';

  /** Initial pass over already-present elements */
  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(processInventoryElement);
  }

  /** MutationObserver — handles variant switches (PDP) and AJAX-loaded cards */
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var mutation = mutations[i];

      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-inventory-level'
      ) {
        // Attribute changed on an existing element (variant switch on PDP)
        processInventoryElement(mutation.target);
      } else if (mutation.type === 'childList') {
        // New nodes added — e.g. SearchSpring AJAX results, infinite scroll
        var added = mutation.addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue; // element nodes only

          if (node.matches && node.matches(SELECTOR)) {
            processInventoryElement(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll(SELECTOR).forEach(processInventoryElement);
          }
        }
      }
    }
  });

  // Run initial pass and start observing once the DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-inventory-level'],
      });
    });
  } else {
    initAll();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-inventory-level'],
    });
  }
})();
